import { Link } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useTabbar } from '@common/providers/useTabbar';
import { Badge } from '@common/ui/Badge';
import { Button } from '@common/ui/buttons/Button';
import { Card } from '@common/ui/Card';
import { Loader } from '@common/ui/Loader';
import { Result } from '@common/ui/Result';
import { useEventEmit } from '@common/utils/events';

import { useAuthStore } from '@entities/auth/stores/useAuthStore';

import {
  useGetPaymentsHistory,
  useGetPaymentsSubscriptionsCurrent,
  usePostPaymentsSubscriptionsCancelAutoRenew,
} from '@services/hooks/PaymentsService';

import {
  CancelSubscriptionDialog,
  PaymentHistory,
  SubscriptionCard,
} from './components';
import styles from './Payments.module.scss';
import { formatDate, isBillingSubscriptionVisible } from './utils';

export const Payments = () => {
  const emit = useEventEmit();
  // emitRef dates from when useEventEmit returned a new function on every
  // render, which made handleCancelConfirm (and the bottom-sheet effect that
  // depends on it) re-set the sheet in a render loop. The emitter has been
  // stable since feed PR 3b, so the ref is no longer needed (as in
  // Premium.tsx).
  const emitRef = useRef(emit);
  const { setBottomSheet } = useTabbar();
  const auth = useAuthStore();

  const [showCancelDialog, setShowCancelDialog] = useState(false);

  useEffect(() => {
    emitRef.current = emit;
  }, [emit]);

  const {
    data: subscription,
    isLoading,
    mutate: mutateSubscription,
  } = useGetPaymentsSubscriptionsCurrent();
  const { trigger: triggerCancelAutoRenew, isMutating } =
    usePostPaymentsSubscriptionsCancelAutoRenew();
  const { data: historyFirstPage } = useGetPaymentsHistory();

  const hasBillingSubscription = isBillingSubscriptionVisible(subscription);
  const premiumUntil = formatDate(auth.user?.premium_until);
  // Premium bought with a one-off card payment (no auto-renewing subscription
  // row exists until автоплатежи is enabled for the shop) — the Stars
  // management hint would be wrong for these users.
  const cardOriginPremium = Boolean(
    historyFirstPage?.payments?.some(
      (payment) =>
        payment.provider === 'yookassa' && payment.status === 'succeeded',
    ),
  );

  const handleCancelConfirm = useCallback(async () => {
    if (!subscription) {
      return;
    }

    try {
      const result = await triggerCancelAutoRenew({
        subscriptionId: subscription.subscriptionId,
      });

      if (result) {
        await mutateSubscription(result, { revalidate: false });
      }

      setShowCancelDialog(false);

      const expiresAt = formatDate(result?.expiresAt);
      emitRef.current('success', {
        title: 'Подписка отменена',
        description: expiresAt
          ? `Доступ сохранится до ${expiresAt}`
          : 'Подписка не будет продлеваться',
      });
    } catch (error) {
      console.error(error);

      emitRef.current('error', {
        title: 'Ошибка',
        description: 'Не удалось отменить подписку. Попробуйте позже',
      });
    }
  }, [mutateSubscription, subscription, triggerCancelAutoRenew]);

  useEffect(() => {
    if (showCancelDialog && subscription) {
      setBottomSheet(
        <CancelSubscriptionDialog
          subscription={subscription}
          loading={isMutating}
          onConfirm={() => {
            void handleCancelConfirm();
          }}
          onKeep={() => setShowCancelDialog(false)}
        />,
      );
    } else {
      setBottomSheet(null);
    }
  }, [
    handleCancelConfirm,
    isMutating,
    setBottomSheet,
    showCancelDialog,
    subscription,
  ]);

  useEffect(() => () => setBottomSheet(null), [setBottomSheet]);

  if (isLoading) {
    return <Loader style={{ margin: '24px auto' }} />;
  }

  return (
    <div className={styles.payments}>
      {hasBillingSubscription && subscription && (
        <SubscriptionCard
          subscription={subscription}
          onCancel={() => setShowCancelDialog(true)}
        />
      )}

      {!hasBillingSubscription && auth.user?.is_premium && (
        <Card testId="payments-premium-stars" className={styles.premiumCard}>
          <div className={styles.header}>
            <div className={styles.title}>Premium</div>
            <Badge
              testId="payments-premium-stars-status"
              color="positiveSecondary"
              text="Активен"
            />
          </div>
          {premiumUntil && (
            <div
              className={styles.row}
              data-test-id="payments-premium-stars-until"
            >
              <span className={styles.label}>Premium активен до</span>
              <span>{premiumUntil}</span>
            </div>
          )}
          {cardOriginPremium ? (
            <div
              className={styles.hint}
              data-test-id="payments-premium-card-hint"
            >
              Оплата прошла разовым платежом — автопродление не подключено,
              дальнейших списаний не будет
            </div>
          ) : (
            <div className={styles.hint}>
              Продлением подписки за Telegram Stars можно управлять в настройках
              Telegram
            </div>
          )}
        </Card>
      )}

      {!hasBillingSubscription && !auth.user?.is_premium && (
        <Card testId="payments-no-premium">
          {/* likes.png (1024x1024) instead of premium.png: the Kiss-branded
              heart is a 231x111 crop that gets stretched into the square
              112px Result container and upscaled ~3x on retina. Same glossy
              heart artwork the Likes/Premium screens already use. */}
          <Result
            src="/icons/3d-icons/likes.png"
            size={500}
            title="Подписка не оформлена"
            description="Оформите Premium, чтобы открыть все возможности"
            button={
              <Link to="/premium" data-test-id="payments-premium-link">
                <Button
                  testId="payments-premium-button"
                  variant="premiumGradient"
                >
                  Подключить Premium
                </Button>
              </Link>
            }
          />
        </Card>
      )}

      <PaymentHistory />
    </div>
  );
};

export default Payments;
